import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  Flame,
  Info,
  Minus,
  Plus,
  RotateCcw,
  X,
} from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from 'components/ui/sheet';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';
import {
  snapshot,
  type ReflectiveProgress,
  type RecurringProgress,
  type SnapshotGoal,
  type SnapshotProgressStep,
  type SnapshotProgressUpdate,
} from 'services/api';

import {
  GoalTargetCelebration,
  QueueCompletionCelebration,
  type GoalCelebrationKind,
  type QueueCompletionSummary,
} from './GoalCelebrations';
import './GoalProgressModal.css';

type UpdateMode = 'quick' | 'correct';
type DateChoice = 'today' | 'yesterday' | 'custom';
type UndoUpdate = Omit<SnapshotProgressUpdate, 'idempotency_key'>;

export const PROGRESS_CELEBRATION_DWELL_MS = 1400;
export const WEEKLY_SEAL_DWELL_MS = 3600;
const REDUCED_MOTION_CELEBRATION_DWELL_MS = 80;

interface SavedUpdate {
  message: string;
  undo?: SnapshotProgressUpdate;
}

interface ProgressCelebration {
  confirmation: GoalProgressConfirmation;
  kind: GoalCelebrationKind;
  stepKey?: string;
  weeklySeal?: boolean;
  targetJustReached?: boolean;
}

export interface GoalProgressConfirmation extends SavedUpdate {
  goal: SnapshotGoal;
}

export interface GoalProgressFlowContext {
  position: number;
  total: number;
  umbrellaLabel?: string;
  progressLabel?: string;
  dateLabel?: string;
  previousUpdate?: GoalProgressConfirmation | null;
  onUndoPrevious?: () => void;
  previousUndoing?: boolean;
  previousUndoError?: string;
  skipSaving?: boolean;
  skipError?: string;
  onSkip?: () => void | Promise<void>;
  onBack?: () => void;
  onNext?: () => void;
  reviewOutcome?: 'saved' | 'skipped';
}

interface GoalProgressModalProps {
  goal: SnapshotGoal | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (goal: SnapshotGoal) => void;
  onDone?: () => void;
  onUpdateComplete?: (confirmation: GoalProgressConfirmation) => void;
  onEditGoal?: (goal: SnapshotGoal) => void;
  flowContext?: GoalProgressFlowContext;
  initialMode?: UpdateMode;
  initialConfirmation?: GoalProgressConfirmation | null;
  initialWeeklySeal?: boolean;
  queueCompletion?: QueueCompletionSummary | null;
  customContent?: React.ReactNode;
  customContentVariant?: 'default' | 'pulse';
  onRequestClose?: () => void;
}

interface GoalEditorProps extends Omit<GoalProgressModalProps, 'queueCompletion' | 'customContent' | 'customContentVariant' | 'onRequestClose'> {
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
  isMobile: boolean;
}

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const localDate = (timezone: string) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

const parseDateOnly = (value?: string | null) => {
  const match = DATE_ONLY_PATTERN.exec(value || '');
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const dateOnlyUtcValue = (value?: string | null) => {
  const match = DATE_ONLY_PATTERN.exec(value || '');
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const addDays = (value: string, amount: number) => {
  const parsed = dateOnlyUtcValue(value);
  if (parsed === null) return value;
  return new Date(parsed + amount * 86_400_000).toISOString().slice(0, 10);
};

const daysBetween = (start: string, end: string) => {
  const startValue = dateOnlyUtcValue(start);
  const endValue = dateOnlyUtcValue(end);
  if (startValue === null || endValue === null) return null;
  return Math.round((endValue - startValue) / 86_400_000);
};

const isWithin = (value: string, start: string, end: string) => value >= start && value <= end;

const formatDateOnly = (value?: string | null) => {
  const parsed = parseDateOnly(value);
  return parsed
    ? new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }).format(parsed)
    : 'the planned start date';
};

const formatShortDate = (value?: string | null) => {
  const parsed = parseDateOnly(value);
  return parsed
    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(parsed)
    : 'the planned date';
};

const formatMonthDay = (value?: string | null) => {
  const parsed = parseDateOnly(value);
  return parsed
    ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(parsed)
    : 'the planned start date';
};

const sentenceCase = (value: string) => value
  ? `${value.charAt(0).toUpperCase()}${value.slice(1)}`
  : 'Progress';

const singularize = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.endsWith('ies')) return `${trimmed.slice(0, -3)}y`;
  if (trimmed.endsWith('ses')) return trimmed.slice(0, -2);
  if (trimmed.endsWith('s') && !trimmed.endsWith('ss')) return trimmed.slice(0, -1);
  return trimmed || 'completion';
};

const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
};

const getErrorMessage = (error: unknown, goal: SnapshotGoal) => {
  if (error && typeof error === 'object') {
    const response = (error as {
      response?: {
        data?: {
          code?: string;
          context?: { start_date?: string };
        };
      };
    }).response;
    if (response?.data?.code === 'goal_not_started') {
      const startDate = response.data.context?.start_date || goal.progress_available_on;
      return `This goal hasn’t started yet. You can update it beginning ${formatMonthDay(startDate)}.`;
    }
    if (response?.data?.code === 'future_progress_date') {
      return 'Choose today or an earlier date before saving.';
    }
    if (response?.data?.code === 'progress_below_zero') {
      return 'That would move this goal below zero. Adjust the total instead.';
    }
  }
  return 'We couldn’t save this update. Please try again.';
};

const getRecurringPeriodPosition = (progress: RecurringProgress) => {
  if (progress.current_period_is_warm_up) return null;
  const start = parseDateOnly(progress.first_full_period_start);
  const current = parseDateOnly(progress.periods.current.start_date);
  if (!start || !current) return Math.min(progress.required_periods, progress.periods_completed + 1);
  if (progress.period_unit === 'month') {
    return Math.min(
      progress.required_periods,
      Math.max(1, (current.getFullYear() - start.getFullYear()) * 12 + current.getMonth() - start.getMonth() + 1),
    );
  }
  const interval = progress.period_unit === 'week' ? 7 : 1;
  const elapsed = Math.floor((current.getTime() - start.getTime()) / 86_400_000);
  return Math.min(progress.required_periods, Math.max(1, Math.floor(elapsed / interval) + 1));
};

const periodNoun = (unit: 'day' | 'week' | 'month') => sentenceCase(unit);

const currentPeriodPhrase = (unit: 'day' | 'week' | 'month') => {
  if (unit === 'day') return 'today';
  return `this ${unit}`;
};

const goalSummary = (goal: SnapshotGoal) => {
  const { progress } = goal;
  if (progress.kind === 'recurring') {
    const position = getRecurringPeriodPosition(progress);
    const overall = position
      ? `${periodNoun(progress.period_unit)} ${position} of ${progress.required_periods}`
      : `Measured plan begins ${formatShortDate(progress.first_full_period_start)}`;
    return `${progress.current_period_value} of ${progress.target_per_period} ${progress.unit} ${currentPeriodPhrase(progress.period_unit)} · ${overall}`;
  }
  if (progress.kind === 'count') return `${progress.value} of ${progress.target} ${progress.unit} completed`;
  if (progress.kind === 'quantity') return `${progress.value} of ${progress.target} ${progress.unit}`;
  if (progress.kind === 'milestone') return `${progress.completed_count} of ${progress.steps.length} milestones done`;
  if (progress.kind === 'deadline') return `${progress.completed_count} of ${progress.steps.length} steps done`;
  if (progress.kind === 'reflective') {
    const prompt = progress.prompt || 'What did you notice this week?';
    return progress.target
      ? `${progress.total_count} of ${progress.target} reflections · ${prompt}`
      : prompt;
  }
  return 'Choose how you want to record progress.';
};

const progressPercent = (value: number, target: number) => target > 0
  ? Math.max(0, Math.min(100, Math.round((value / target) * 100)))
  : 0;

const completionLabelFor = (goal: SnapshotGoal) => {
  if (goal.progress.kind === 'recurring' || goal.progress.kind === 'count') {
    return goal.progress.completion_label || singularize(goal.progress.unit);
  }
  return 'completion';
};

const isWeeklySealThreshold = (
  before: SnapshotGoal,
  after: SnapshotGoal,
  update: Omit<SnapshotProgressUpdate, 'idempotency_key'>,
) => {
  if (before.progress.kind !== 'recurring' || after.progress.kind !== 'recurring') return false;
  if (after.progress.period_unit !== 'week' || after.progress.target_reached) return false;
  if (!before.progress.periods.current.counts_toward_goal) return false;
  if (before.progress.periods.current.target_ever_reached) return false;
  if (!isWithin(
    update.occurred_on,
    before.progress.periods.current.start_date,
    before.progress.periods.current.end_date,
  )) return false;
  return before.progress.current_period_value < before.progress.target_per_period
    && after.progress.current_period_value >= after.progress.target_per_period;
};

const weeklySealSavedMessage = (progress: RecurringProgress) => (
  `Week complete — ${progress.periods_completed} of ${progress.required_periods} weeks.`
);

const QueueProgress = ({ position, total, label }: { position: number; total: number; label?: string }) => (
  <div className="goal-checkin__queue" aria-label={label || `Goal ${position} of ${total}`}>
    <div className="goal-checkin__segments" aria-hidden="true">
      {Array.from({ length: total }, (_, index) => (
        <span
          key={index}
          className={`goal-checkin__segment ${index < position ? 'goal-checkin__segment--handled' : ''}`}
        />
      ))}
    </div>
    <span className="goal-checkin__position">{label || `Goal ${position} of ${total}`}</span>
  </div>
);

const SavedBanner = ({ flowContext }: { flowContext: GoalProgressFlowContext }) => {
  if (!flowContext.previousUpdate) return null;
  return (
    <>
      <div className="goal-checkin__saved-banner" data-testid="goal-checkin-saved-banner" role="status" aria-live="polite">
        <span className="goal-checkin__saved-icon" aria-hidden="true"><Check size={13} strokeWidth={3} /></span>
        <span className="goal-checkin__saved-copy">{flowContext.previousUpdate.message}</span>
        {flowContext.previousUpdate.undo && flowContext.onUndoPrevious ? (
          <button
            type="button"
            className="goal-checkin__undo"
            disabled={flowContext.previousUndoing}
            onClick={flowContext.onUndoPrevious}
          >
            {flowContext.previousUndoing ? 'Undoing…' : 'Undo'}
          </button>
        ) : null}
      </div>
      {flowContext.previousUndoError ? (
        <div className="goal-checkin__error-detail" role="alert">
          <AlertCircle size={14} /><span>{flowContext.previousUndoError}</span>
        </div>
      ) : null}
    </>
  );
};

interface GoalHeaderProps {
  goal: SnapshotGoal;
  flowContext?: GoalProgressFlowContext;
  headingRef: React.RefObject<HTMLHeadingElement>;
  onCancel: () => void;
}

const GoalHeader = ({ goal, flowContext, headingRef, onCancel }: GoalHeaderProps) => (
  <header className="goal-checkin__header" data-testid="goal-checkin-header">
    <div className="goal-checkin__topline">
      <div className="goal-checkin__header-copy">
        <span className="goal-checkin__eyebrow">{flowContext?.umbrellaLabel || 'Quick goal update'}</span>
        {flowContext?.dateLabel ? <span className="goal-checkin__date">{flowContext.dateLabel}</span> : null}
        {flowContext ? (
          <div className="goal-checkin__queue-navigation">
            <QueueProgress position={flowContext.position} total={flowContext.total} label={flowContext.progressLabel} />
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="goal-checkin__close"
        aria-label={flowContext ? 'Close check-in' : 'Close goal update'}
        onClick={onCancel}
      >
        <X size={17} />
      </button>
    </div>
    <h2
      ref={headingRef}
      tabIndex={flowContext ? -1 : undefined}
      className="goal-checkin__title"
    >
      {goal.title}
    </h2>
    <p className="goal-checkin__summary">{goalSummary(goal)}</p>
  </header>
);

const DiscardDraftConfirmation = ({
  onKeepEditing,
  onDiscard,
}: {
  onKeepEditing: () => void;
  onDiscard: () => void;
}) => {
  const keepEditingRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    keepEditingRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="goal-checkin__discard-backdrop">
      <div className="goal-checkin__discard" role="alertdialog" aria-modal="true" aria-labelledby="goal-checkin-discard-title" aria-describedby="goal-checkin-discard-description">
        <span className="goal-checkin__eyebrow">Unsaved update</span>
        <h2 id="goal-checkin-discard-title" className="goal-checkin__discard-title">Discard this update?</h2>
        <p id="goal-checkin-discard-description" className="goal-checkin__discard-description">Your note or progress adjustment has not been saved yet.</p>
        <div className="goal-checkin__discard-actions">
          <button ref={keepEditingRef} type="button" className="goal-checkin__primary" onClick={onKeepEditing}>Keep editing</button>
          <button type="button" className="goal-checkin__quiet-action" onClick={onDiscard}>Discard update</button>
        </div>
      </div>
    </div>
  );
};

const RecurringVisual = ({
  progress,
  celebrating = false,
  weeklySeal = false,
}: {
  progress: RecurringProgress;
  celebrating?: boolean;
  weeklySeal?: boolean;
}) => {
  const position = getRecurringPeriodPosition(progress);
  const dotCount = Math.min(progress.target_per_period, 12);
  const extra = Math.max(0, progress.current_period_value - progress.target_per_period);
  const periodCount = Math.min(progress.required_periods, 12);
  const successfulPeriods = Math.min(progress.qualifying_periods, Math.max(0, (position || 1) - 1));
  const sealProgress = progress.consecutive ? progress.current_streak : progress.qualifying_periods;
  return (
    <div
      className={`goal-checkin__progress-panel ${weeklySeal ? 'goal-checkin__progress-panel--weekly-seal' : ''}`}
      data-testid={weeklySeal ? 'goal-weekly-seal' : undefined}
    >
      <div className="goal-checkin__progress-row">
        <span className="goal-checkin__help goal-checkin__help--strong">{sentenceCase(currentPeriodPhrase(progress.period_unit))}</span>
        <span className="goal-checkin__help">{progress.current_period_value} of {progress.target_per_period} {progress.unit}</span>
      </div>
      {progress.target_per_period <= 12 ? (
        <div className="goal-checkin__dots" aria-label={`${progress.current_period_value} of ${progress.target_per_period} ${progress.unit} complete ${currentPeriodPhrase(progress.period_unit)}`}>
          {Array.from({ length: dotCount }, (_, index) => (
            <span
              key={index}
              className={`goal-checkin__dot ${index < progress.current_period_value ? 'goal-checkin__dot--done' : 'goal-checkin__dot--todo'} ${celebrating && index === progress.current_period_value - 1 ? 'goal-checkin__dot--just-done' : ''}`}
              data-testid={celebrating && index === progress.current_period_value - 1 ? 'goal-progress-new-completion' : undefined}
              aria-hidden="true"
            >
              {index < progress.current_period_value ? <Check size={16} strokeWidth={3} /> : null}
            </span>
          ))}
        </div>
      ) : (
        <div className="goal-checkin__bar" role="progressbar" aria-valuemin={0} aria-valuemax={progress.target_per_period} aria-valuenow={Math.min(progress.current_period_value, progress.target_per_period)} aria-valuetext={`${progress.current_period_value} of ${progress.target_per_period} ${progress.unit}${extra > 0 ? `, ${extra} extra` : ''}`}>
          <span className="goal-checkin__bar-fill" style={{ width: `${progressPercent(progress.current_period_value, progress.target_per_period)}%` }} />
        </div>
      )}
      {extra > 0 ? <p className="goal-checkin__help goal-checkin__help--strong">Target met · {extra} extra</p> : null}
      {weeklySeal ? (
        <div className="goal-checkin__weekly-connector" aria-hidden="true">
          <span className="goal-checkin__weekly-connector-track" />
          <span className="goal-checkin__weekly-connector-flow" />
        </div>
      ) : null}
      <div className="goal-checkin__progress-row">
        <span className="goal-checkin__help goal-checkin__help--strong">{progress.required_periods}-{progress.period_unit} plan</span>
        <span className="goal-checkin__help">
          {position ? `${periodNoun(progress.period_unit)} ${position} of ${progress.required_periods}` : 'Getting started'}
        </span>
      </div>
      {progress.required_periods <= 12 ? (
        <div className="goal-checkin__periods" aria-label={`${progress.qualifying_periods} qualifying periods of ${progress.required_periods}`}>
          {Array.from({ length: periodCount }, (_, index) => {
            const displayPosition = index + 1;
            const outcome = progress.period_history?.find((item) => item.position === displayPosition);
            const current = position === displayPosition;
            const complete = outcome?.met_target
              ?? (index < successfulPeriods || (current && progress.current_period_value >= progress.target_per_period));
            const missed = Boolean(outcome?.is_closed && !outcome.met_target);
            const sealing = weeklySeal && current;
            const reachingGoal = !weeklySeal && celebrating && progress.target_reached && current && complete;
            return (
              <span
                key={displayPosition}
                className={`goal-checkin__period ${complete ? 'goal-checkin__period--done' : ''} ${missed ? 'goal-checkin__period--missed' : ''} ${current ? 'goal-checkin__period--current' : ''} ${sealing ? 'goal-checkin__period--sealing' : ''} ${reachingGoal ? 'goal-checkin__period--goal-reached' : ''}`}
                data-testid={sealing ? 'goal-weekly-seal-period' : reachingGoal ? 'goal-target-reached-period' : missed ? `goal-period-${displayPosition}-missed` : undefined}
                aria-hidden="true"
              >
                {sealing ? <span className="goal-checkin__period-halo" /> : null}
                {sealing ? <span className="goal-checkin__period-number">{displayPosition}</span> : null}
                {complete ? (
                  <span className={sealing ? 'goal-checkin__period-check' : undefined}>
                    <Check size={12} strokeWidth={4} />
                  </span>
                ) : displayPosition}
              </span>
            );
          })}
        </div>
      ) : (
        <div className="goal-checkin__bar" role="progressbar" aria-valuemin={0} aria-valuemax={progress.required_periods} aria-valuenow={Math.min(progress.qualifying_periods, progress.required_periods)} aria-valuetext={`${progress.qualifying_periods} qualifying periods of ${progress.required_periods}`}>
          <span className="goal-checkin__bar-fill" style={{ width: `${progressPercent(progress.qualifying_periods, progress.required_periods)}%` }} />
        </div>
      )}
      {weeklySeal ? (
        <div className="goal-checkin__weekly-copy" role="status" aria-live="polite">
          {progress.consecutive ? (
            <span className="goal-checkin__streak-pill">
              <Flame size={13} fill="currentColor" strokeWidth={0} aria-hidden="true" />
              {progress.current_streak}-week streak
            </span>
          ) : null}
          <p className="goal-checkin__weekly-headline">You showed up this week.</p>
          <span className="goal-checkin__weekly-summary">
            {progress.consecutive
              ? `${progress.current_streak}-week streak · ${sealProgress} of ${progress.required_periods} weeks complete`
              : `${sealProgress} of ${progress.required_periods} successful weeks`}
          </span>
        </div>
      ) : null}
      {progress.current_period_is_warm_up ? (
        <div className="goal-checkin__soft-note goal-checkin__soft-note--blue">
          <Info size={16} />
          <span>This is a getting-started period. Your full measured periods begin {formatDateOnly(progress.first_full_period_start)}.</span>
        </div>
      ) : null}
    </div>
  );
};

const ReflectiveVisual = ({
  progress,
  celebrating = false,
}: {
  progress: ReflectiveProgress;
  celebrating?: boolean;
}) => {
  if (!progress.target) {
    return (
      <div className="goal-checkin__progress-panel">
        <div className="goal-checkin__progress-row">
          <span className="goal-checkin__help goal-checkin__help--strong">Ongoing practice</span>
          <span className="goal-checkin__help">{progress.total_count} reflection{progress.total_count === 1 ? '' : 's'} recorded</span>
        </div>
        <p className="goal-checkin__help">{progress.month_count} reflection{progress.month_count === 1 ? '' : 's'} this month · no finish line</p>
      </div>
    );
  }
  const completed = progress.total_count;
  const completedDots = Math.min(completed, progress.target);
  const extra = Math.max(0, completed - progress.target);
  return (
    <div className="goal-checkin__progress-panel">
      <div className="goal-checkin__progress-row">
        <span className="goal-checkin__help goal-checkin__help--strong">Reflections</span>
        <span className="goal-checkin__help">{completed} of {progress.target} complete</span>
      </div>
      {progress.target <= 12 ? (
        <div className="goal-checkin__dots" aria-label={`${completed} of ${progress.target} reflections complete`}>
          {Array.from({ length: progress.target }, (_, index) => {
            const complete = index < completedDots;
            const justCompleted = celebrating && index === completedDots - 1;
            return (
              <span
                key={index}
                className={`goal-checkin__dot ${complete ? 'goal-checkin__dot--done' : 'goal-checkin__dot--todo'} ${justCompleted ? 'goal-checkin__dot--just-done' : ''}`}
                data-testid={justCompleted ? 'goal-progress-new-completion' : complete ? 'goal-reflection-completion' : undefined}
                aria-hidden="true"
              >
                {complete ? <Check size={16} strokeWidth={3} /> : null}
              </span>
            );
          })}
        </div>
      ) : (
        <div className="goal-checkin__bar" role="progressbar" aria-valuemin={0} aria-valuemax={progress.target} aria-valuenow={Math.min(completed, progress.target)} aria-valuetext={`${completed} of ${progress.target} reflections complete${extra > 0 ? `, ${extra} extra` : ''}`}>
          <span className="goal-checkin__bar-fill" style={{ width: `${progressPercent(completed, progress.target)}%` }} />
        </div>
      )}
      {extra > 0 ? <p className="goal-checkin__help goal-checkin__help--strong">Target met · {extra} extra</p> : null}
      <p className="goal-checkin__help">{progress.month_count} reflection{progress.month_count === 1 ? '' : 's'} this month</p>
    </div>
  );
};

interface StepListProps {
  steps: SnapshotProgressStep[];
  saving: boolean;
  onReopen: (step: SnapshotProgressStep) => void;
  celebratingStepKey?: string;
}

const StepList = ({ steps, saving, onReopen, celebratingStepKey }: StepListProps) => {
  const firstIncomplete = steps.find((step) => !step.completed);
  return (
    <ul className="goal-checkin__steps">
      {steps.map((step) => {
        const next = step.key === firstIncomplete?.key;
        return (
          <li key={step.key} className={`goal-checkin__step ${step.completed ? 'goal-checkin__step--done' : ''} ${step.key === celebratingStepKey ? 'goal-checkin__step--just-done' : ''}`} data-testid={step.key === celebratingStepKey ? 'goal-progress-new-step' : undefined}>
            <span
              className={`goal-checkin__step-glyph ${step.completed ? 'goal-checkin__step-glyph--done' : ''} ${next ? 'goal-checkin__step-glyph--next' : ''}`}
              aria-hidden="true"
            >
              {step.completed ? <Check size={12} strokeWidth={3.5} /> : null}
            </span>
            <span className="goal-checkin__step-label">{step.label}</span>
            {step.completed ? (
              <button type="button" className="goal-checkin__reopen" disabled={saving} onClick={() => onReopen(step)}>
                Reopen
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

const DeadlineNote = ({ goal }: { goal: SnapshotGoal }) => {
  if (goal.progress.kind !== 'deadline' || !goal.progress.target_date) return null;
  const today = localDate(goal.timezone);
  const remaining = daysBetween(today, goal.progress.target_date);
  if (remaining === null) return null;
  if (remaining < 0) {
    const elapsed = Math.abs(remaining);
    return (
      <div className="goal-checkin__soft-note goal-checkin__soft-note--sand">
        <AlertCircle size={15} />
        <span>The deadline passed {elapsed} {elapsed === 1 ? 'day' : 'days'} ago. You can still finish the remaining steps, or adjust the date.</span>
      </div>
    );
  }
  return (
    <div className="goal-checkin__soft-note goal-checkin__soft-note--blue">
      <Clock3 size={15} />
      <span>Due {formatShortDate(goal.progress.target_date)} · <strong>{remaining === 0 ? 'Due today' : `${remaining} ${remaining === 1 ? 'day' : 'days'} left`}</strong></span>
    </div>
  );
};

const FlowNavigation = ({ flowContext, saving, allowSkip = true }: {
  flowContext?: GoalProgressFlowContext;
  saving: boolean;
  allowSkip?: boolean;
}) => {
  if (!flowContext) return null;
  const reviewing = Boolean(flowContext.reviewOutcome);
  if (!flowContext.onBack && !flowContext.onNext && (!allowSkip || !flowContext.onSkip)) return null;
  return (
    <div className="goal-checkin__footer">
      {flowContext.onBack ? (
        <button type="button" className="goal-checkin__link-action" disabled={saving} onClick={flowContext.onBack}>
          <ArrowLeft size={15} /> Back
        </button>
      ) : <span />}
      {reviewing && flowContext.onNext ? (
        <button type="button" className="goal-checkin__link-action" disabled={saving} onClick={flowContext.onNext}>
          Next goal <ArrowRight size={15} />
        </button>
      ) : allowSkip && flowContext.onSkip ? (
        <button type="button" className="goal-checkin__link-action" disabled={saving || flowContext.skipSaving} onClick={() => { void flowContext.onSkip?.(); }}>
          {flowContext.skipSaving ? 'Skipping…' : 'Skip until tomorrow'} <ArrowRight size={15} />
        </button>
      ) : <span />}
    </div>
  );
};

const FlowFooter = ({ flowContext, saving }: {
  flowContext?: GoalProgressFlowContext;
  saving: boolean;
}) => {
  if (!flowContext) return null;
  return (
    <>
      <p className="goal-checkin__reassurance">
        {flowContext.reviewOutcome === 'saved'
          ? 'This goal was already updated in this check-in. You can undo or adjust it.'
          : flowContext.reviewOutcome === 'skipped'
            ? 'This goal is skipped until tomorrow. You can still update it or move through your check-in history.'
            : 'You can record progress today or skip this goal until tomorrow.'}
      </p>
      {flowContext.skipError ? (
        <div className="goal-checkin__error-detail" role="alert">
          <AlertCircle size={14} /><span>{flowContext.skipError}</span>
        </div>
      ) : null}
      <FlowNavigation flowContext={flowContext} saving={saving} />
    </>
  );
};

const GoalEditor = ({
  goal,
  onSaved,
  onCancel,
  onDone,
  onUpdateComplete,
  onEditGoal,
  flowContext,
  initialMode = 'quick',
  initialConfirmation,
  initialWeeklySeal = false,
  onDirtyChange,
  isMobile,
}: GoalEditorProps) => {
  const initialProgressCelebration = useMemo<ProgressCelebration | null>(() => initialConfirmation
    ? {
      confirmation: initialConfirmation,
      kind: initialConfirmation.goal.progress.kind,
      weeklySeal: initialWeeklySeal,
      targetJustReached: false,
    }
    : null, [initialConfirmation, initialWeeklySeal]);
  const [progressCelebration, setProgressCelebration] = useState<ProgressCelebration | null>(initialProgressCelebration);
  const [weeklySealConfirmation, setWeeklySealConfirmation] = useState<GoalProgressConfirmation | null>(null);
  const displayGoal = progressCelebration?.confirmation.goal ?? weeklySealConfirmation?.goal ?? goal;
  const recurringProgress = displayGoal?.progress.kind === 'recurring' ? displayGoal.progress : null;
  const countProgress = displayGoal?.progress.kind === 'count' ? displayGoal.progress : null;
  const quantityProgress = displayGoal?.progress.kind === 'quantity' ? displayGoal.progress : null;
  const currentTallyValue = recurringProgress?.periods.current.value
    ?? countProgress?.value
    ?? quantityProgress?.value
    ?? 0;
  const goalToday = displayGoal ? localDate(displayGoal.timezone) : '';
  const initialOccurrenceDate = recurringProgress?.periods.current.occurred_on || goalToday;
  const initialDateChoice: DateChoice = initialOccurrenceDate === goalToday
    ? 'today'
    : initialOccurrenceDate === addDays(goalToday, -1)
      ? 'yesterday'
      : 'custom';
  const [value, setValue] = useState(currentTallyValue);
  const [quantityAmount, setQuantityAmount] = useState('');
  const [mode, setMode] = useState<UpdateMode>(initialMode);
  const [note, setNote] = useState('');
  const [occurrenceDate, setOccurrenceDate] = useState(initialOccurrenceDate);
  const [dateChoice, setDateChoice] = useState<DateChoice>(initialDateChoice);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedUpdate, setSavedUpdate] = useState<SavedUpdate | null>(null);
  const [inlineUpdate, setInlineUpdate] = useState<SavedUpdate | null>(null);
  const [targetConfirmation, setTargetConfirmation] = useState<GoalProgressConfirmation | null>(null);
  const [targetAcknowledged, setTargetAcknowledged] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const flowPosition = flowContext?.position;

  useEffect(() => {
    setValue(currentTallyValue);
    setQuantityAmount('');
    setMode(initialMode);
    setNote('');
    setOccurrenceDate(initialOccurrenceDate);
    setDateChoice(initialDateChoice);
    setSaving(false);
    setError('');
    setSavedUpdate(null);
    setInlineUpdate(null);
    setTargetConfirmation(null);
    setTargetAcknowledged(false);
    setWeeklySealConfirmation(null);
    setProgressCelebration(initialProgressCelebration);
    setIdempotencyKey(newIdempotencyKey());
    // A same-goal refresh should not erase confirmation UI after saving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal?.id, initialMode, initialProgressCelebration]);

  useEffect(() => {
    if (flowPosition === undefined) return;
    headingRef.current?.focus();
  }, [flowPosition, goal?.id]);

  useEffect(() => {
    if (!progressCelebration) return undefined;
    const reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = window.setTimeout(() => {
      const { confirmation, weeklySeal, targetJustReached } = progressCelebration;
      setProgressCelebration(null);
      if (targetJustReached) {
        setTargetConfirmation(confirmation);
      } else if (onUpdateComplete) {
        onUpdateComplete(confirmation);
      } else if (weeklySeal) {
        setWeeklySealConfirmation(confirmation);
      } else {
        setSavedUpdate({ message: confirmation.message, undo: confirmation.undo });
      }
    }, reducedMotion
      ? REDUCED_MOTION_CELEBRATION_DWELL_MS
      : progressCelebration.weeklySeal
        ? WEEKLY_SEAL_DWELL_MS
        : PROGRESS_CELEBRATION_DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [onUpdateComplete, progressCelebration]);

  const steps = displayGoal?.progress.kind === 'milestone' || displayGoal?.progress.kind === 'deadline'
    ? displayGoal.progress.steps
    : [];
  const firstIncomplete = steps.find((step) => !step.completed);
  const completionLabel = displayGoal ? completionLabelFor(displayGoal) : 'completion';
  const quickActionAccessibleLabel = progressCelebration || weeklySealConfirmation
    ? `${sentenceCase(completionLabel)} marked complete`
    : saving
    ? `Marking one ${completionLabel} complete`
    : `Mark one ${completionLabel} complete`;
  const reviewWasSaved = flowContext?.reviewOutcome === 'saved';
  const reachedTargetConfirmation = targetConfirmation ?? (
    !progressCelebration
    && !targetAcknowledged
    && displayGoal?.status === 'active'
    && displayGoal.progress.target_reached
      ? flowContext?.previousUpdate?.goal.id === displayGoal.id
        ? flowContext.previousUpdate
        : { goal: displayGoal, message: 'Target reached.' }
      : null
  );

  const selectedRecurringPeriod = useMemo(() => {
    if (!recurringProgress) return null;
    if (isWithin(occurrenceDate, recurringProgress.periods.current.start_date, recurringProgress.periods.current.end_date)) {
      return { key: 'current' as const, period: recurringProgress.periods.current };
    }
    if (isWithin(occurrenceDate, recurringProgress.periods.previous.start_date, recurringProgress.periods.previous.end_date)) {
      return { key: 'previous' as const, period: recurringProgress.periods.previous };
    }
    return null;
  }, [occurrenceDate, recurringProgress]);
  const draftIsDirty = Boolean(displayGoal)
    && !progressCelebration
    && !weeklySealConfirmation
    && !savedUpdate
    && !targetConfirmation
    && (
      mode === 'correct'
        ? value !== currentTallyValue || note.length > 0 || occurrenceDate !== initialOccurrenceDate
        : quantityAmount.length > 0 || (displayGoal?.progress.kind === 'reflective' && note.length > 0)
    );

  useEffect(() => {
    onDirtyChange(draftIsDirty);
  }, [draftIsDirty, onDirtyChange]);

  if (!goal || !displayGoal) return null;

  const updateOccurrenceDate = (nextDate: string, choice: DateChoice) => {
    setOccurrenceDate(nextDate);
    setDateChoice(choice);
    setError('');
    if (!recurringProgress) return;
    if (isWithin(nextDate, recurringProgress.periods.current.start_date, recurringProgress.periods.current.end_date)) {
      setValue(recurringProgress.periods.current.value);
    } else if (isWithin(nextDate, recurringProgress.periods.previous.start_date, recurringProgress.periods.previous.end_date)) {
      setValue(recurringProgress.periods.previous.value);
    }
  };

  const saveUpdate = async (
    update: Omit<SnapshotProgressUpdate, 'idempotency_key'>,
    message: (updated: SnapshotGoal) => string,
    undo?: UndoUpdate,
    options?: { stayOpen?: boolean },
  ) => {
    setSaving(true);
    setError('');
    try {
      const refreshed = await snapshot.updateGoalProgress(goal.id, {
        ...update,
        idempotency_key: idempotencyKey,
      });
      const confirmation: GoalProgressConfirmation = {
        goal: refreshed,
        message: message(refreshed),
        undo: undo ? { ...undo, idempotency_key: newIdempotencyKey() } : undefined,
      };
      const weeklySeal = isWeeklySealThreshold(displayGoal, refreshed, update);
      onSaved(refreshed);
      setIdempotencyKey(newIdempotencyKey());
      if (options?.stayOpen) {
        setInlineUpdate({ message: confirmation.message, undo: confirmation.undo });
      } else {
        setProgressCelebration({
          confirmation,
          kind: refreshed.progress.kind,
          stepKey: 'milestone_key' in update ? update.milestone_key : undefined,
          weeklySeal,
          targetJustReached: !displayGoal.progress.target_reached && refreshed.progress.target_reached,
        });
      }
      return refreshed;
    } catch (saveError) {
      setError(getErrorMessage(saveError, goal));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const quickLog = async () => {
    if (recurringProgress) {
      const occurredOn = recurringProgress.periods.current.occurred_on;
      await saveUpdate(
        { occurred_on: occurredOn, operation: 'increment', amount: 1 },
        (updated) => {
          const progress = updated.progress.kind === 'recurring' ? updated.progress : recurringProgress;
          if (isWeeklySealThreshold(displayGoal, updated, { occurred_on: occurredOn, operation: 'increment', amount: 1 })) {
            return weeklySealSavedMessage(progress);
          }
          return `${sentenceCase(completionLabel)} marked complete — ${progress.current_period_value} of ${progress.target_per_period} ${currentPeriodPhrase(progress.period_unit)}.`;
        },
        { occurred_on: occurredOn, operation: 'decrement', amount: 1 },
      );
      return;
    }
    if (countProgress) {
      const occurredOn = localDate(goal.timezone);
      await saveUpdate(
        { occurred_on: occurredOn, operation: 'increment', amount: 1 },
        (updated) => {
          const progress = updated.progress.kind === 'count' ? updated.progress : countProgress;
          return `${sentenceCase(completionLabel)} marked complete — ${progress.value} of ${progress.target} total.`;
        },
        { occurred_on: occurredOn, operation: 'decrement', amount: 1 },
      );
      return;
    }
    if (quantityProgress) {
      const amount = Number(quantityAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setError('Enter an amount greater than zero.');
        return;
      }
      const occurredOn = localDate(goal.timezone);
      await saveUpdate(
        { occurred_on: occurredOn, operation: 'increment', amount },
        (updated) => {
          const progress = updated.progress.kind === 'quantity' ? updated.progress : quantityProgress;
          return `${amount} ${progress.unit} added — ${progress.value} of ${progress.target} total.`;
        },
        { occurred_on: occurredOn, operation: 'decrement', amount },
      );
      return;
    }
    if ((goal.progress.kind === 'milestone' || goal.progress.kind === 'deadline') && firstIncomplete) {
      const occurredOn = localDate(goal.timezone);
      await saveUpdate(
        { occurred_on: occurredOn, milestone_key: firstIncomplete.key, completed: true },
        () => `Completed “${firstIncomplete.label}.”`,
        { occurred_on: occurredOn, milestone_key: firstIncomplete.key, completed: false },
      );
    }
  };

  const reopenStep = async (step: SnapshotProgressStep) => {
    const occurredOn = localDate(goal.timezone);
    await saveUpdate(
      { occurred_on: occurredOn, milestone_key: step.key, completed: false },
      () => `Reopened “${step.label}.”`,
      { occurred_on: occurredOn, milestone_key: step.key, completed: true },
      { stayOpen: true },
    );
  };

  const saveCorrection = async () => {
    if (recurringProgress) {
      if (!selectedRecurringPeriod?.period.can_update) {
        setError(`Choose a date between ${formatShortDate(recurringProgress.periods.previous.start_date)} and today.`);
        return;
      }
      const priorValue = selectedRecurringPeriod.period.value;
      const periodCopy = selectedRecurringPeriod.key === 'current'
        ? currentPeriodPhrase(recurringProgress.period_unit)
        : `last ${recurringProgress.period_unit}`;
      const recurringUpdate = { occurred_on: occurrenceDate, operation: 'set' as const, value, note };
      await saveUpdate(
        recurringUpdate,
        (updated) => updated.progress.kind === 'recurring'
          && isWeeklySealThreshold(displayGoal, updated, recurringUpdate)
          ? weeklySealSavedMessage(updated.progress)
          : `${sentenceCase(periodCopy)} is now ${value} of ${recurringProgress.target_per_period}.`,
        { occurred_on: occurrenceDate, operation: 'set', value: priorValue },
      );
      return;
    }
    if (countProgress || quantityProgress) {
      const priorValue = countProgress?.value ?? quantityProgress?.value ?? 0;
      const unit = countProgress?.unit ?? quantityProgress?.unit ?? '';
      await saveUpdate(
        { occurred_on: occurrenceDate, operation: 'set', value, note },
        () => `Your total is now ${value} ${unit}.`,
        { occurred_on: occurrenceDate, operation: 'set', value: priorValue },
      );
      return;
    }
    if (goal.progress.kind === 'reflective') {
      await saveUpdate(
        { occurred_on: localDate(goal.timezone), note },
        (updated) => updated.progress.kind === 'reflective' && updated.progress.target
          ? `Reflection saved — ${updated.progress.total_count} of ${updated.progress.target}.`
          : 'Reflection saved.',
      );
    }
  };

  const undoStandalone = async () => {
    if (!savedUpdate?.undo) return;
    setSaving(true);
    setError('');
    try {
      const refreshed = await snapshot.updateGoalProgress(goal.id, savedUpdate.undo);
      onSaved(refreshed);
      setSavedUpdate({ message: 'Update undone.' });
    } catch (undoError) {
      setError(getErrorMessage(undoError, goal));
    } finally {
      setSaving(false);
    }
  };

  const finishTargetReached = () => {
    if (!reachedTargetConfirmation) return;
    if (targetConfirmation) {
      if (onUpdateComplete) onUpdateComplete(reachedTargetConfirmation);
      else (onDone || onCancel)();
      return;
    }
    setTargetAcknowledged(true);
  };

  const markGoalComplete = async () => {
    if (!reachedTargetConfirmation) return;
    setSaving(true);
    setError('');
    try {
      const completedGoal = await snapshot.updateGoalStatus(reachedTargetConfirmation.goal.id, 'completed');
      onSaved(completedGoal);
      const completedConfirmation = { ...reachedTargetConfirmation, goal: completedGoal };
      if (onUpdateComplete) onUpdateComplete(completedConfirmation);
      else (onDone || onCancel)();
    } catch {
      setError('We couldn’t mark this goal complete. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const today = localDate(goal.timezone);
  const yesterday = addDays(today, -1);
  const recurringDateMin = recurringProgress?.periods.previous.can_update
    ? recurringProgress.periods.previous.start_date
    : recurringProgress?.periods.current.start_date;
  const dateMin = recurringDateMin || goal.progress_available_on || goal.start_date || today;
  const selectedPeriodPosition = recurringProgress ? getRecurringPeriodPosition(recurringProgress) : null;
  const selectedPeriodPhrase = recurringProgress && selectedRecurringPeriod?.key === 'previous'
    ? `last ${recurringProgress.period_unit}`
    : recurringProgress
      ? currentPeriodPhrase(recurringProgress.period_unit)
      : '';
  const correctionUnit = recurringProgress?.unit ?? countProgress?.unit ?? quantityProgress?.unit ?? 'items';
  const correctionTarget = recurringProgress?.target_per_period ?? countProgress?.target ?? quantityProgress?.target ?? null;
  const canSaveCorrection = goal.can_accept_progress && (
    goal.progress.kind === 'reflective'
      ? note.trim().length > 0
      : Boolean(occurrenceDate) && value >= 0 && (recurringProgress ? Boolean(selectedRecurringPeriod?.period.can_update) : true)
  );
  const celebrating = Boolean(progressCelebration);
  const weeklySeal = Boolean(
    recurringProgress
    && (progressCelebration?.weeklySeal || weeklySealConfirmation),
  );
  const busy = saving || celebrating;

  if (reachedTargetConfirmation) {
    const reachedGoal = reachedTargetConfirmation.goal;
    return (
      <div className="goal-checkin goal-checkin--target-reached">
        <div className="goal-celebration__topline">
          {flowContext ? <QueueProgress position={flowContext.position} total={flowContext.total} label={flowContext.progressLabel} /> : <span />}
          <button type="button" className="goal-checkin__close" aria-label="Close goal celebration" onClick={onCancel}>
            <X size={17} />
          </button>
        </div>
        <GoalTargetCelebration
          goal={reachedGoal}
          error={error}
          saving={saving}
          onKeepGoing={finishTargetReached}
          onMarkComplete={() => { void markGoalComplete(); }}
        />
        <FlowNavigation flowContext={flowContext} saving={saving} allowSkip={false} />
      </div>
    );
  }

  if (savedUpdate) {
    return (
      <div className="goal-checkin goal-checkin__standalone-saved">
        <span className="goal-checkin__standalone-saved-icon" aria-hidden="true"><Check size={24} /></span>
        <div role="status" aria-live="polite">
          <span className="goal-checkin__eyebrow">Goal progress updated</span>
          <h2 className="goal-checkin__title">{savedUpdate.message}</h2>
          <p className="goal-checkin__summary">Your goal history has been updated.</p>
        </div>
        {error ? (
          <div className="goal-checkin__soft-note goal-checkin__soft-note--orange" role="alert">
            <AlertCircle size={16} /><span>{error}</span>
          </div>
        ) : null}
        <div className="goal-checkin__adjust-footer">
          {savedUpdate.undo ? (
            <button type="button" className="goal-checkin__link-action" disabled={saving} onClick={() => { void undoStandalone(); }}>
              <RotateCcw size={15} /> Undo
            </button>
          ) : <span />}
          <button type="button" className="goal-checkin__primary goal-checkin__save-update" disabled={saving} onClick={onDone || onCancel}>Done</button>
        </div>
      </div>
    );
  }

  if (mode === 'correct') {
    return (
      <div className="goal-checkin">
        <header className="goal-checkin__header goal-checkin__header--adjust">
          <div className="goal-checkin__adjust-topline">
            <button
              type="button"
              className="goal-checkin__link-action goal-checkin__back"
              onClick={() => initialMode === 'quick' ? setMode('quick') : onCancel()}
            >
              <ArrowLeft size={18} /> {initialMode === 'quick' ? 'Back' : 'Cancel'}
            </button>
            <span className="goal-checkin__eyebrow">Adjust · {goal.goal_type}</span>
          </div>
          <h2 className="goal-checkin__title goal-checkin__adjust-title">{goal.title}</h2>
        </header>
        {recurringProgress ? (
          <div className="goal-checkin__soft-note goal-checkin__soft-note--blue">
            <Info size={16} />
            <span>
              You’re editing <strong>{selectedPeriodPhrase}’s</strong> total
              {selectedPeriodPosition ? ` (${periodNoun(recurringProgress.period_unit)} ${selectedPeriodPosition} of ${recurringProgress.required_periods})` : ''}.
              {' '}It counts {recurringProgress.unit} for the whole {recurringProgress.period_unit}, not just today.
            </span>
          </div>
        ) : (
          <div className="goal-checkin__soft-note goal-checkin__soft-note--blue">
            <Info size={16} />
            <span>You’re editing the goal’s running total. Earlier updates stay safely in your goal history.</span>
          </div>
        )}

        <div className="goal-checkin__field">
          <label className="goal-checkin__label" htmlFor="goal-checkin-total">
            {recurringProgress
              ? `${sentenceCase(correctionUnit)} completed ${selectedPeriodPhrase}`
              : `Current ${correctionUnit} total`}
          </label>
          {quantityProgress ? (
            <div className="goal-checkin__amount-row">
              <input
                id="goal-checkin-total"
                className="goal-checkin__input"
                type="number"
                min="0"
                step="any"
                value={value}
                onChange={(event) => setValue(Number(event.target.value))}
              />
              <span className="goal-checkin__help">{correctionUnit}</span>
            </div>
          ) : (
            <div className="goal-checkin__stepper-row">
              <div className="goal-checkin__stepper">
                <button type="button" className="goal-checkin__stepper-button" aria-label={`Remove one ${singularize(correctionUnit)}`} onClick={() => setValue((current) => Math.max(0, current - 1))}><Minus size={18} /></button>
                <output id="goal-checkin-total" className="goal-checkin__stepper-value" aria-live="polite">{value}</output>
                <button type="button" className="goal-checkin__stepper-button" aria-label={`Add one ${singularize(correctionUnit)}`} onClick={() => setValue((current) => current + 1)}><Plus size={18} /></button>
              </div>
              <span className="goal-checkin__help">
                Current total: <strong>{value}{correctionTarget !== null ? ` of ${correctionTarget}` : ''}</strong>{recurringProgress ? ` ${selectedPeriodPhrase}` : ''}
              </span>
            </div>
          )}
        </div>

        <fieldset className="goal-checkin__field">
          <legend className="goal-checkin__label">When did this activity happen?</legend>
          <div className="goal-checkin__date-options">
            <button type="button" className={`goal-checkin__date-option ${dateChoice === 'today' ? 'goal-checkin__date-option--selected' : ''}`} aria-pressed={dateChoice === 'today'} onClick={() => updateOccurrenceDate(today, 'today')}>Today</button>
            <button type="button" className={`goal-checkin__date-option ${dateChoice === 'yesterday' ? 'goal-checkin__date-option--selected' : ''}`} aria-pressed={dateChoice === 'yesterday'} disabled={yesterday < dateMin} onClick={() => updateOccurrenceDate(yesterday, 'yesterday')}>Yesterday</button>
            <button type="button" className={`goal-checkin__date-option ${dateChoice === 'custom' ? 'goal-checkin__date-option--selected' : ''}`} aria-pressed={dateChoice === 'custom'} aria-label="Choose another date" onClick={() => setDateChoice('custom')}>
              <CalendarDays size={16} /><span className={isMobile ? 'sr-only' : ''}>Choose another date</span>
            </button>
          </div>
          {dateChoice === 'custom' ? (
            <input
              className="goal-checkin__input"
              type="date"
              aria-label="Activity date"
              min={dateMin}
              max={today}
              value={occurrenceDate}
              onChange={(event) => updateOccurrenceDate(event.target.value, 'custom')}
            />
          ) : null}
          <span className="goal-checkin__help goal-checkin__date-helper">The date describes the activity you’re logging. Your total above covers the whole tracking period.</span>
        </fieldset>

        <div className="goal-checkin__field">
          <label className="goal-checkin__label" htmlFor="goal-checkin-note">Note <span className="goal-checkin__help">(optional)</span></label>
          <textarea id="goal-checkin-note" className="goal-checkin__textarea" rows={2} placeholder="Anything worth remembering about this one…" value={note} onChange={(event) => setNote(event.target.value)} />
        </div>

        {error ? (
          <div className="goal-checkin__error-detail" role="alert">
            <AlertCircle size={14} /><span>{error}</span>
          </div>
        ) : null}

        <div className="goal-checkin__adjust-footer">
          <button type="button" className="goal-checkin__link-action" disabled={saving} onClick={() => initialMode === 'quick' ? setMode('quick') : onCancel()}>Cancel</button>
          <button type="button" className="goal-checkin__primary goal-checkin__save-update" disabled={saving || !canSaveCorrection} onClick={() => { void saveCorrection(); }}>
            {saving ? <><span className="goal-checkin__spinner" />Saving…</> : 'Save update'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`goal-checkin ${celebrating ? `goal-checkin--celebrating goal-checkin--celebrating-${progressCelebration?.kind}` : ''} ${weeklySeal ? 'goal-checkin--weekly-seal' : ''}`}>
      {inlineUpdate ? (
        <div className="goal-checkin__saved-banner" role="status" aria-live="polite">
          <span className="goal-checkin__saved-icon" aria-hidden="true"><Check size={13} strokeWidth={3} /></span>
          <span className="goal-checkin__saved-copy">{inlineUpdate.message}</span>
        </div>
      ) : null}
      {flowContext ? <SavedBanner flowContext={flowContext} /> : null}
      <div className={flowContext ? 'goal-checkin__next' : undefined} data-testid={flowContext ? 'goal-checkin-next' : undefined}>
        <GoalHeader goal={displayGoal} flowContext={flowContext} headingRef={headingRef} onCancel={onCancel} />

      {progressCelebration ? (
        <span className="goal-checkin__sr-only" role="status" aria-live="polite">
          {progressCelebration.confirmation.message}
        </span>
      ) : null}

      {flowContext?.reviewOutcome === 'skipped' ? (
        <div className="goal-checkin__soft-note goal-checkin__soft-note--blue" role="status">
          <Clock3 size={16} />
          <span><strong>Skipped until tomorrow</strong><br />Nothing was lost. You can still update this goal before returning to the current one.</span>
        </div>
      ) : null}

      {!displayGoal.can_accept_progress ? (
        <div className="goal-checkin__soft-note goal-checkin__soft-note--blue">
          <Clock3 size={16} />
          <span><strong>Tracking starts {formatDateOnly(goal.progress_available_on)}</strong><br />You can begin updating then, or edit the goal’s start date.</span>
        </div>
      ) : null}

      {displayGoal.can_accept_progress && recurringProgress ? <RecurringVisual progress={recurringProgress} celebrating={progressCelebration?.kind === 'recurring'} weeklySeal={weeklySeal} /> : null}

      {displayGoal.can_accept_progress && countProgress ? (
        <>
          <div className="goal-checkin__metric">
            <span className="goal-checkin__metric-value">{countProgress.value}</span>
            <span className="goal-checkin__help">of {countProgress.target} {countProgress.unit} · {progressPercent(countProgress.value, countProgress.target)}%</span>
          </div>
          <div className="goal-checkin__bar" role="progressbar" aria-valuemin={0} aria-valuemax={countProgress.target} aria-valuenow={Math.min(countProgress.value, countProgress.target)} aria-valuetext={`${countProgress.value} of ${countProgress.target} ${countProgress.unit}${countProgress.value > countProgress.target ? `, ${countProgress.value - countProgress.target} extra` : ''}`}>
            <span className="goal-checkin__bar-fill" style={{ width: `${progressPercent(countProgress.value, countProgress.target)}%` }} />
          </div>
          {countProgress.value > countProgress.target ? <span className="goal-checkin__help goal-checkin__help--strong">Target met · {countProgress.value - countProgress.target} extra</span> : null}
        </>
      ) : null}

      {displayGoal.can_accept_progress && quantityProgress && !reviewWasSaved ? (
        <>
          <div className="goal-checkin__bar" style={{ marginTop: 12 }} role="progressbar" aria-valuemin={0} aria-valuemax={quantityProgress.target} aria-valuenow={Math.min(quantityProgress.value, quantityProgress.target)} aria-valuetext={`${quantityProgress.value} of ${quantityProgress.target} ${quantityProgress.unit}${quantityProgress.value > quantityProgress.target ? `, ${quantityProgress.value - quantityProgress.target} extra` : ''}`}>
            <span className="goal-checkin__bar-fill" style={{ width: `${progressPercent(quantityProgress.value, quantityProgress.target)}%` }} />
          </div>
          {quantityProgress.value > quantityProgress.target ? <span className="goal-checkin__help goal-checkin__help--strong">Target met · {quantityProgress.value - quantityProgress.target} extra</span> : null}
          <div className="goal-checkin__field">
            <label className="goal-checkin__label" htmlFor="goal-checkin-quantity">Add {quantityProgress.unit}</label>
            <div className="goal-checkin__amount-row">
              <input
                id="goal-checkin-quantity"
                className="goal-checkin__input"
                type="number"
                min="0.01"
                step="any"
                inputMode="decimal"
                aria-label="How much would you like to add?"
                value={quantityAmount}
                onChange={(event) => { setQuantityAmount(event.target.value); setError(''); }}
              />
              <span className="goal-checkin__help">{quantityProgress.unit}</span>
            </div>
          </div>
        </>
      ) : null}

      {displayGoal.can_accept_progress && (displayGoal.progress.kind === 'milestone' || displayGoal.progress.kind === 'deadline') ? (
        <>
          {displayGoal.progress.kind === 'deadline' ? <DeadlineNote goal={displayGoal} /> : null}
          <StepList steps={displayGoal.progress.steps} saving={busy} celebratingStepKey={progressCelebration?.stepKey} onReopen={(step) => { void reopenStep(step); }} />
        </>
      ) : null}

      {displayGoal.can_accept_progress && displayGoal.progress.kind === 'reflective' && !reviewWasSaved ? (
        <>
          <ReflectiveVisual progress={displayGoal.progress} celebrating={celebrating} />
          <div className="goal-checkin__field">
            <textarea
              id="goal-checkin-reflection"
              className="goal-checkin__textarea"
              rows={isMobile ? 6 : 4}
              aria-label="This week’s reflection"
              placeholder="A sentence or two is plenty…"
              value={note}
              onChange={(event) => { setNote(event.target.value); setError(''); }}
            />
          </div>
        </>
      ) : null}

      {error ? (
        <div className="goal-checkin__soft-note goal-checkin__soft-note--orange" role="alert">
          <AlertCircle size={16} /><span>{error}</span>
        </div>
      ) : null}

        <div className="goal-checkin__action-dock" data-testid="goal-checkin-action-dock">
          {displayGoal.can_accept_progress && (recurringProgress || countProgress) && !reviewWasSaved ? (
            <button type="button" className={`goal-checkin__primary ${celebrating || weeklySealConfirmation ? 'goal-checkin__primary--confirmed' : ''}`} aria-label={quickActionAccessibleLabel} aria-busy={busy} disabled={busy || Boolean(weeklySealConfirmation)} onClick={() => { void quickLog(); }}>
              {celebrating || weeklySealConfirmation ? <><Check size={17} strokeWidth={3} />{sentenceCase(completionLabel)} marked complete</> : saving ? <><span className="goal-checkin__spinner" />Marking…</> : error ? 'Try again' : `Mark 1 ${completionLabel} complete`}
            </button>
          ) : null}

          {displayGoal.can_accept_progress && quantityProgress && !reviewWasSaved ? (
            <button type="button" className={`goal-checkin__primary ${celebrating ? 'goal-checkin__primary--confirmed' : ''}`} aria-label={`Add ${quantityProgress.unit}`} aria-busy={busy} disabled={busy || !quantityAmount} onClick={() => { void quickLog(); }}>
              {celebrating ? <><Check size={17} strokeWidth={3} />Progress added</> : saving ? <><span className="goal-checkin__spinner" />Saving…</> : error ? 'Try again' : 'Add distance'}
            </button>
          ) : null}

          {displayGoal.can_accept_progress && (displayGoal.progress.kind === 'milestone' || displayGoal.progress.kind === 'deadline') && firstIncomplete && !reviewWasSaved ? (
            <button type="button" className={`goal-checkin__primary goal-checkin__primary--compact ${celebrating ? 'goal-checkin__primary--confirmed' : ''}`} aria-label={`Complete ${displayGoal.progress.kind === 'milestone' ? 'milestone' : 'step'}: ${firstIncomplete.label}`} aria-busy={busy} disabled={busy} onClick={() => { void quickLog(); }}>
              {celebrating ? <><Check size={17} strokeWidth={3} />Step completed</> : saving ? <><span className="goal-checkin__spinner" />Saving…</> : error ? 'Try again' : `Complete “${firstIncomplete.label}”`}
            </button>
          ) : null}

          {displayGoal.can_accept_progress && displayGoal.progress.kind === 'reflective' && !reviewWasSaved ? (
            <>
              <button type="button" className={`goal-checkin__primary ${celebrating ? 'goal-checkin__primary--confirmed' : ''}`} aria-busy={busy} disabled={busy || !note.trim()} onClick={() => { void saveCorrection(); }}>
                {celebrating ? <><span className="goal-checkin__confirmation-mark" aria-hidden="true"><Check size={17} strokeWidth={3} /><span className="goal-checkin__reflection-ripple" /></span>Reflection saved</> : saving ? <><span className="goal-checkin__spinner" />Saving…</> : error ? 'Try again' : 'Save reflection'}
              </button>
              {!note.trim() ? <span className="goal-checkin__help" style={{ marginTop: 6, textAlign: 'center' }}>Add a few words to save.</span> : null}
            </>
          ) : null}

          {displayGoal.can_accept_progress && (recurringProgress || countProgress) ? (
            <button type="button" className="goal-checkin__quiet-action" disabled={busy || Boolean(weeklySealConfirmation)} onClick={() => { setMode('correct'); setError(''); }}>
              Add note or adjust
            </button>
          ) : null}

          {displayGoal.can_accept_progress && displayGoal.progress.kind === 'deadline' && daysBetween(localDate(displayGoal.timezone), displayGoal.progress.target_date || '')! < 0 && onEditGoal ? (
            <button type="button" className="goal-checkin__quiet-action" disabled={saving} onClick={() => onEditGoal(displayGoal)}>Adjust deadline</button>
          ) : null}

          {displayGoal.can_accept_progress ? <FlowFooter flowContext={flowContext} saving={busy || Boolean(weeklySealConfirmation)} /> : (
            <button type="button" className="goal-checkin__quiet-action" onClick={onCancel}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};

export const GoalProgressModal = ({
  goal,
  onOpenChange,
  onSaved,
  onDone,
  onUpdateComplete,
  onEditGoal,
  flowContext,
  initialMode = 'quick',
  initialConfirmation,
  initialWeeklySeal = false,
  queueCompletion,
  customContent,
  customContentVariant = 'default',
  onRequestClose,
}: GoalProgressModalProps) => {
  const { isMobile } = useResponsiveLayout();
  const open = Boolean(goal || queueCompletion || customContent);
  const currentIsCustom = Boolean(customContent);
  const showingQueueCompletion = Boolean(queueCompletion);
  const [editorDirty, setEditorDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const retainedEditorRef = useRef<React.ReactNode>(null);
  const retainedCustomLayoutRef = useRef(false);
  const retainedVariantRef = useRef(customContentVariant);

  useEffect(() => {
    setEditorDirty(false);
    setConfirmDiscard(false);
  }, [goal?.id, open, showingQueueCompletion]);

  const requestClose = () => {
    if (customContent && onRequestClose) {
      onRequestClose();
      return;
    }
    if (editorDirty) {
      setConfirmDiscard(true);
      return;
    }
    onOpenChange(false);
  };
  const editorProps = {
    goal,
    onOpenChange,
    onSaved,
    onDone,
    onUpdateComplete,
    onEditGoal,
    flowContext,
    initialMode,
    initialConfirmation,
    initialWeeklySeal,
    isMobile,
    onDirtyChange: setEditorDirty,
    onCancel: requestClose,
  };
  const editor = customContent || (queueCompletion ? (
    <div className="goal-checkin goal-checkin--queue-completion" data-testid="goal-checkin-queue-completion">
      <QueueCompletionCelebration {...queueCompletion} />
    </div>
  ) : (
    <div key={goal?.id || 'no-goal'}>
      <GoalEditor {...editorProps} />
    </div>
  ));
  const renderedEditor = open ? editor : retainedEditorRef.current;
  const renderedAsCustom = open ? currentIsCustom : retainedCustomLayoutRef.current;
  const renderedVariant = open ? customContentVariant : retainedVariantRef.current;
  useLayoutEffect(() => {
    if (!open) return;
    retainedEditorRef.current = editor;
    retainedCustomLayoutRef.current = currentIsCustom;
    retainedVariantRef.current = customContentVariant;
  });
  const discardConfirmation = confirmDiscard ? (
    <DiscardDraftConfirmation
      onKeepEditing={() => setConfirmDiscard(false)}
      onDiscard={() => {
        setConfirmDiscard(false);
        setEditorDirty(false);
        onOpenChange(false);
      }}
    />
  ) : null;

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
        <SheetContent data-testid="goal-progress-modal-surface" side="bottom" className={`goal-checkin-sheet ${renderedAsCustom ? 'goal-checkin-sheet--custom' : ''} ${renderedAsCustom && renderedVariant === 'pulse' ? 'goal-checkin-sheet--pulse' : ''}`} overlayClassName="goal-checkin-overlay">
          <SheetTitle className="sr-only">{renderedAsCustom ? 'Daily Check-in' : 'Update goal progress'}</SheetTitle>
          <SheetDescription className="sr-only">{renderedAsCustom ? 'Complete or review your Daily Pulse.' : 'Record a progress update for this goal.'}</SheetDescription>
          {renderedEditor}
          {discardConfirmation}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) requestClose(); }}>
      <DialogContent data-testid="goal-progress-modal-surface" hideCloseButton className={`goal-checkin-dialog ${renderedAsCustom ? 'goal-checkin-dialog--custom' : ''} ${renderedAsCustom && renderedVariant === 'pulse' ? 'goal-checkin-dialog--pulse' : ''}`} overlayClassName="goal-checkin-overlay">
        <DialogHeader className="sr-only">
          <DialogTitle>{renderedAsCustom ? 'Daily Check-in' : 'Update goal progress'}</DialogTitle>
          <DialogDescription>{renderedAsCustom ? 'Complete or review your Daily Pulse.' : 'Record a progress update for this goal.'}</DialogDescription>
        </DialogHeader>
        {renderedEditor}
        {discardConfirmation}
      </DialogContent>
    </Dialog>
  );
};
